import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classes from './SelfieCamera.module.css'

export interface SelfieCameraProps {
  /** The photo taken: the middle square as the person saw it (mirrored), a JPEG of at most `size` pixels a side */
  onShot: (photo: File) => void
  /** No front camera, or no permission for it */
  onError: () => void
  size?: number
}

/**
 * The front camera live, mirrored like a phone's own, with an oval for the face and a shutter: a photo taken now, never
 * one picked from the phone's pictures. Until the first frame plays — the permission prompt, a slow camera — it says so
 * instead of sitting black. The camera stops when it closes
 */
export function SelfieCamera({ onShot, onError, size = 640 }: SelfieCameraProps) {
  const { t } = useTranslation()
  const video = useRef<HTMLVideoElement>(null)
  const [live, setLive] = useState(false)
  const failed = useRef(onError)
  failed.current = onError

  useEffect(() => {
    let stream: MediaStream | null = null
    let closed = false
    if (!navigator.mediaDevices?.getUserMedia) {
      failed.current()
      return
    }
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 1280 } }, audio: false })
      .then((s) => {
        stream = s
        if (closed) for (const track of s.getTracks()) track.stop()
        else if (video.current) video.current.srcObject = s
      })
      .catch(() => !closed && failed.current())
    return () => {
      closed = true
      for (const track of stream?.getTracks() ?? []) track.stop()
    }
  }, [])

  const shoot = () => {
    const v = video.current
    if (!v?.videoWidth) return
    const side = Math.min(v.videoWidth, v.videoHeight)
    const out = Math.min(size, side)
    const canvas = document.createElement('canvas')
    canvas.width = out
    canvas.height = out
    const context = canvas.getContext('2d')
    if (!context) return onError()
    context.translate(out, 0)
    context.scale(-1, 1)
    context.drawImage(v, (v.videoWidth - side) / 2, (v.videoHeight - side) / 2, side, side, 0, 0, out, out)
    canvas.toBlob((blob) => blob && onShot(new File([blob], 'selfie.jpg', { type: 'image/jpeg' })), 'image/jpeg', 0.85)
  }

  return (
    <div className={classes.viewfinder}>
      <video ref={video} className={classes.video} autoPlay playsInline muted onPlaying={() => setLive(true)} />
      <span className={classes.oval} aria-hidden="true" />
      {live ? (
        <button type="button" className={classes.shutter} onClick={shoot} aria-label={t('photo.take')} />
      ) : (
        <span className={classes.starting}>{t('scan.starting')}</span>
      )}
    </div>
  )
}
